/**
 * Convert viem results (bigint / named structs) into web3.js-shaped values:
 * bigints become decimal strings, structs get both named and numeric keys.
 */
export const serialize = (value: unknown): any => {
  if (typeof value === "bigint") {
    return value.toString();
  }

  if (Array.isArray(value)) {
    const serialized = value.map(serialize);
    // viem attaches named tuple keys onto the array; keep them for web3-shaped access
    for (const key of Object.keys(value)) {
      if (!/^\d+$/.test(key)) {
        (serialized as any)[key] = serialize((value as any)[key]);
      }
    }
    return serialized;
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    const out: Record<string | number, unknown> = {};

    for (const [key, nested] of entries) {
      out[key] = serialize(nested);
    }

    const hasNumericKeys = entries.some(([key]) => /^\d+$/.test(key));
    if (!hasNumericKeys) {
      entries.forEach((_, index) => {
        out[index] = out[entries[index][0]];
      });
    }

    return out;
  }

  return value;
};

export const toBigInt = (value: string | number | bigint): bigint => {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") return BigInt(Math.trunc(value));
  return BigInt(value);
};

export const field = <T = any>(value: any, name: string, index: number): T =>
  value?.[name] ?? value?.[index];

/** Coerce viem/web3-shaped values to a real boolean so `if (status)` never treats arrays/objects as fulfilled. */
export const toBool = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "bigint") return value !== 0n;
  if (typeof value === "number") return value !== 0 && !Number.isNaN(value);
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1") return true;
    if (normalized === "false" || normalized === "0" || normalized === "") {
      return false;
    }
  }
  if (Array.isArray(value)) return toBool(value[0]);
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if ("isFulfilled" in record) return toBool(record.isFulfilled);
    if ("fulfilled" in record) return toBool(record.fulfilled);
    if ("isPending" in record) return toBool(record.isPending);
    if (0 in record) return toBool(record[0]);
  }
  return false;
};
